import React, { useState } from "react";
import { Input, Button, Space, Typography, Tooltip, message, Checkbox } from "antd";
const { Text } = Typography;

import { Droppable, Draggable } from "react-beautiful-dnd";

import PropTypes from "prop-types";
import { PlusOutlined, HolderOutlined, EditOutlined, DeleteOutlined, StopOutlined } from "@ant-design/icons";
import { ClassificationStatus, getTextStyle } from "./utils";

export const ClassificationNode = ({ 
	item, 
	index, 
	depth, 
	isSelected = false,
	showArchived = false,
	selectedItems = {},
	onAddClassificationToNode = () => {},
	onRenameNode = () => {},
	onDeleteNode = () => {},
	onToggleSelection = () => {},
	onSelectWithChildren = () => {},
}) => {	
	const [isEditing, setIsEditing] = useState(false);		
	const [editedValue, setEditedValue] = useState("");

	// Función para iniciar la edición
	const startEditing = (currentValue) => {		
		setIsEditing(true);
		setEditedValue(currentValue);
	};

	// Función para guardar el cambio
	const saveEditing = () => {
		if (editedValue.trim() === "") {
			message.error("Por favor, ingresa un valor válido.");
			return;
		}
		
		onRenameNode(item.id, editedValue);
		setIsEditing(false);
		setEditedValue("");
	};
		
	const visibleChildren = item.children?.filter(child => showArchived || !child.archived) || [];
  
	return (
		<Draggable 
			key={item.id} 
			draggableId={item.id} 
			index={index}
			isDragDisabled={item.archived && !showArchived}
		>
			{(provided) => (
				<div
					ref={provided.innerRef}
					{...provided.draggableProps}
					style={{
						display: "flex",
						flexDirection: "column",
						margin: "8px 0",
						padding: 8,						
						border: "1px solid #ddd",						
						borderRadius: 4,
						marginLeft: depth * 12,
						opacity: item.status === ClassificationStatus.ARCHIVED && !showArchived ? 0.7 : 1,
						...provided.draggableProps.style,
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<Button
							{...provided.dragHandleProps}
							icon={<HolderOutlined />}
							size="small"
							type="text"
							style={{ cursor: "grab" }}
						/>						

						<Checkbox
							checked={isSelected}
							onChange={() => onToggleSelection(item.id)}
						/>

						{isEditing ? (
							<Input
								value={editedValue}
								showCount
								onChange={(e) => {
									const filteredValue = e.target.value
										.replace(/-/g, "_");
										
									setEditedValue(filteredValue);
								}}								
								onPressEnter={() => {
									saveEditing();									
								}}
								onBlur={() => {
									saveEditing();									
								}}								
								maxLength={20}
								style={{ width: 120 }}
								autoFocus
							/>
						) : (
							<div style={{ flexGrow: 1, textAlign: "left" }}>
								<Text strong style={getTextStyle(item.status, item.archived)}>
									{index + 1}. {item.classification_name}
								</Text>
							</div>
						)}

						<Space>
							<Tooltip title="Editar">
								<Button
									icon={<EditOutlined />}
									type="primary"
									ghost
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										if (isEditing) {
											saveEditing();
										} else {
											startEditing(item.classification_name);
										}
									}}
								/>
							</Tooltip>
							<Tooltip title={item.isInUse ? "No se puede eliminar porque esta en uso" : "Eliminar"}>
								<Button
									icon={item.isInUse ? <StopOutlined /> : <DeleteOutlined />}
									size="small"
									danger									
									onClick={() => onDeleteNode(item.id)}
									disabled={item.isInUse || !item.archived}
								/>
							</Tooltip>
						</Space>
					</div>

					{visibleChildren.length > 0 && (
						<Droppable droppableId={item.id} type={`DEPTH-${depth + 1}`}>
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{visibleChildren.map((child, i) => (
										<ClassificationNode
											key={child.id}
											item={child}
											index={i}
											parentId={item.id}
											depth={depth + 1}
											onRenameNode={onRenameNode}
											onDeleteNode={onDeleteNode}
											isSelected={selectedItems[child.id] || false}
											selectedItems={selectedItems}
											onToggleSelection={onToggleSelection}
											onSelectWithChildren={onSelectWithChildren}
											showArchived={showArchived}											
										/>
									))}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					)}

					{/* Button to add a new classification into a sub-category */}
					{depth === 1 && <Button 
						icon={<PlusOutlined />}
						type="dashed" 						
						block
						onClick={() => onAddClassificationToNode(item.id)}
					>
						Nueva Clasificación
					</Button>}		
				</div>
			)}
		</Draggable>
	);
};

ClassificationNode.propTypes = { // item, index, depth
	item: PropTypes.object.isRequired,
	index: PropTypes.string.isRequired,
	depth: PropTypes.number.isRequired,
	isSelected: PropTypes.bool,
	showArchived: PropTypes.bool,
	selectedItems: PropTypes.object,
	onAddClassificationToNode: PropTypes.func,
	onRenameNode: PropTypes.func,
	onDeleteNode: PropTypes.func,
	onToggleSelection: PropTypes.func,
	onSelectWithChildren: PropTypes.func,
};